/* Revenue Dashboard `/admin/revenue` — Wave 6 · Batch 3 · Cluster D.
 *
 * The single dashboard the operator opens every morning. Complementary
 * to /admin/churn (which drills into WHY people leave) — this one asks
 * "how big is the business and is it growing?".
 *
 * Data source: /api/admin/revenue/overview (memoised 5m, refresh=1 bypasses).
 *
 * Cluster D chrome sweep changes:
 *   - Legacy CSS variables and hex literals migrated to the DS token set.
 *   - KpiCard local component deleted; 4 headline KPI cards migrated to
 *     `_primitives.Stat` (MRR flagged `highlight` for accent-strong value;
 *     others neutral). Per-card icon-tint feature was chrome only and is
 *     replaced by the shared Stat treatment (matches Overview anchor).
 *   - Chip local component deleted; 4 chip call-sites (range / metric /
 *     ts-range / granularity) migrated to `_primitives.SegmentedFilter`.
 *   - Refresh action swapped to `_primitives.GhostButton`.
 *   - Export CSV action swapped to `_primitives.AccentButton`.
 *   - Row-2 conversion + churn cards, Row-3 time-series card, Row-4
 *     coupon-impact card, and Row-5 churn-reasons card wrapped in
 *     `_primitives.Section` shells (denser hairline chrome).
 *   - Recharts chart palette from Batch 2 (Line stroke = accent-strong,
 *     grid = admin-border, axis ticks = ink-subtle, tooltip = tokenised,
 *     danger branch on churn > 0.05 = --ds-color-danger) preserved
 *     byte-exact — verified intact, no reapplication needed.
 *
 * BYTE-EXACT PRESERVATIONS (business logic):
 *   - 4 API endpoints: /admin/revenue/overview, /admin/revenue/timeseries,
 *     /admin/revenue/export, /admin/churn.
 *   - Churn danger threshold: `churn.net_churn_rate > 0.05` — literal 0.05
 *     unchanged.
 *   - Aggregation logic: `money`, `pct`, `fmtInt` helpers, MRR/ARR/LTV/ARR
 *     read from `overview.*` byte-exact.
 *   - Timeseries transform: `series.map(row => ({date, value: metric === "mrr"
 *     ? row.value / 100 : row.value}))` — byte-exact.
 *   - Coupon-impact bar-chart transform: `[{name:"Before",value:...},{name:"After",value:...}]`
 *     byte-exact.
 *   - CSV export blob type / filename / cleanup sequence: byte-exact.
 *   - `trackEvent("Revenue Dashboard Viewed", { range })` (fires once per
 *     mount) — byte-exact.
 *   - All `revenue-*` testids preserved.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Coins,
  Download,
  ExternalLink,
  Loader2,
  Percent,
  RefreshCw,
  TrendingUp,
  Users,
} from "lucide-react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Link } from "react-router-dom";
import { toast } from "sonner";

import AdminLayout from "@/admin/AdminLayout";
import AdminPageHelp from "@/design-system/components/AdminPageHelp";
import { api, formatApiError } from "@/api/client";
import { trackEvent } from "@/lib/analytics";
import {
  Section,
  Stat,
  SegmentedFilter,
  GhostButton,
  AccentButton,
} from "@/admin/_primitives";

const RANGE_OPTIONS = [
  { value: "7d", label: "7d" },
  { value: "30d", label: "30d" },
  { value: "90d", label: "90d" },
  { value: "365d", label: "365d" },
];
const METRIC_OPTIONS = [
  { value: "mrr", label: "MRR ($)" },
  { value: "new_subscriptions", label: "New subs" },
  { value: "cancellations", label: "Cancellations" },
];
const GRANULARITY_OPTIONS = [
  { value: "day", label: "day" },
  { value: "week", label: "week" },
  { value: "month", label: "month" },
];

function money(cents) {
  if (cents === null || cents === undefined) return "—";
  const dollars = Number(cents) / 100;
  return dollars.toLocaleString("en-US", { style: "currency", currency: "USD" });
}
function pct(v) {
  if (v === null || v === undefined) return "—";
  return `${(Number(v) * 100).toFixed(1)}%`;
}
function fmtInt(n) {
  return Number(n || 0).toLocaleString("en-US");
}

export default function RevenueDashboard() {
  const [range, setRange] = useState("30d");
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState(null);

  // Time-series chart controls
  const [metric, setMetric] = useState("mrr");
  const [tsRange, setTsRange] = useState("90d");
  const [granularity, setGranularity] = useState("week");
  const [series, setSeries] = useState([]);
  const [seriesLoading, setSeriesLoading] = useState(false);

  // Top churn reasons (pulled from /admin/churn for parity)
  const [churnReasons, setChurnReasons] = useState([]);

  const load = useCallback(async (opts = {}) => {
    const bust = !!opts.refresh;
    if (bust) setRefreshing(true); else setLoading(true);
    setError(null);
    try {
      const { data } = await api.get("/admin/revenue/overview", {
        params: { range, ...(bust ? { refresh: 1 } : {}) },
      });
      setOverview(data);
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      if (bust) setRefreshing(false); else setLoading(false);
    }
  }, [range]);

  const loadSeries = useCallback(async () => {
    setSeriesLoading(true);
    try {
      const { data } = await api.get("/admin/revenue/timeseries", {
        params: { metric, range: tsRange, granularity },
      });
      setSeries(data.series || []);
    } catch {
      setSeries([]);
    } finally {
      setSeriesLoading(false);
    }
  }, [metric, tsRange, granularity]);

  const loadChurn = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/churn", { params: { range: "30d" } });
      setChurnReasons((data.reasons || []).slice(0, 5));
    } catch {
      setChurnReasons([]);
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadSeries(); }, [loadSeries]);
  useEffect(() => { loadChurn(); }, [loadChurn]);
  useEffect(() => {
    trackEvent("Revenue Dashboard Viewed", { range });
    // Deliberately fire once per mount, not per-range.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const chartData = useMemo(() => series.map(row => ({
    date: row.date,
    value: metric === "mrr" ? row.value / 100 : row.value,
  })), [series, metric]);

  const exportCsv = useCallback(async () => {
    setExporting(true);
    try {
      const resp = await api.get("/admin/revenue/export", {
        params: { range, format: "csv" },
        responseType: "blob",
      });
      const blob = new Blob([resp.data], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `revenue-${range}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Revenue CSV exported.");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setExporting(false);
    }
  }, [range]);

  const mrr = overview?.mrr;
  const active = overview?.active_subscribers;
  const trial = overview?.trial_conversion;
  const churn = overview?.churn_rate;
  const ltv = overview?.ltv;
  const coup = overview?.coupon_impact;

  return (
    <AdminLayout
      title="Revenue"
      subtitle={
        <>
          MRR, LTV, and churn across all paying members.
          {overview?.cached ? (
            <span className="ml-2 text-[10px] uppercase tracking-[0.14em]" style={{ color: "var(--ds-color-ink-muted)" }}>
              · cached {Math.round((overview.cache_expires_in_seconds || 0) / 60)}m left
            </span>
          ) : null}
        </>
      }
      actions={
        <div className="flex items-center gap-2 flex-wrap">
          <SegmentedFilter
            options={RANGE_OPTIONS}
            value={range}
            onChange={setRange}
            testIdPrefix="revenue-range"
          />
          <GhostButton
            data-testid="revenue-refresh-btn"
            onClick={() => load({ refresh: true })}
            disabled={refreshing}
          >
            {refreshing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
            Refresh
          </GhostButton>
          <AccentButton
            data-testid="revenue-export-btn"
            onClick={exportCsv}
            disabled={exporting}
          >
            {exporting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
            Export CSV
          </AccentButton>
        </div>
      }
    >
      <AdminPageHelp
        title="Revenue"
        purpose="Snapshot of gross revenue, MRR, ARR, and refund/chargeback activity, plus per-plan and per-partner-referral breakdowns."
        configures="Nothing on this page changes live data — it's a read-only reporting surface driven by Stripe payment events."
        impact="Not applicable — read-only reporting. No user- or system-visible change results from viewing this page."
        safety="Yes — read-only. Filters and date ranges reset on page reload."
      />
      <div data-testid="admin-revenue-page" className="max-w-[1400px] min-w-0">

        {error ? (
          <div
            data-testid="revenue-error"
            className="rounded-[var(--ds-radius-input)] p-4 mb-6 text-sm"
            style={{
              background: "var(--ds-color-danger-soft)",
              border: "1px solid var(--ds-color-danger)",
              color: "var(--ds-color-danger)",
            }}
          >
            {error}
          </div>
        ) : null}

        {loading && !overview ? (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            {[0, 1, 2, 3].map(i => (
              <div
                key={i}
                className="rounded-[var(--ds-radius-card)] h-32 animate-pulse"
                style={{
                  background: "var(--ds-color-surface)",
                  border: "1px solid var(--ds-color-admin-border)",
                }}
              />
            ))}
          </div>
        ) : null}

        {overview ? (
          <>
            {/* Row 1 — headline KPIs */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              <Stat
                testid="revenue-mrr-card"
                label="MRR"
                value={money(mrr.mrr_cents)}
                icon={Coins}
                highlight
                sub={`Monthly ${money(mrr.monthly_subs_cents)} · Annual ${money(mrr.annual_subs_cents)}`}
              />
              <Stat
                testid="revenue-arr-card"
                label="ARR"
                value={money(overview.arr_cents)}
                icon={TrendingUp}
                sub="12 × MRR (annualised)"
              />
              <Stat
                testid="revenue-active-subs-card"
                label="Active subscribers"
                value={fmtInt(active.total)}
                icon={Users}
                sub={`Monthly ${fmtInt(active.monthly)} · Annual ${fmtInt(active.annual)} · Trialing ${fmtInt(active.trialing)}`}
              />
              <Stat
                testid="revenue-ltv-card"
                label="LTV"
                value={ltv.ltv_cents === null ? "n/a" : money(ltv.ltv_cents)}
                icon={Percent}
                sub={
                  ltv.ltv_cents === null
                    ? "Undefined until churn > 0"
                    : `ARPU ${money(ltv.avg_revenue_per_user_cents)} ÷ ${pct(ltv.monthly_churn_rate)} monthly churn`
                }
              />
            </div>

            {/* Row 2 — conversion + churn (bespoke because churn uses conditional danger color) */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <div
                data-testid="revenue-conversion-card"
                className="rounded-[var(--ds-radius-card)] p-5"
                style={{
                  background: "var(--ds-color-surface)",
                  border: "1px solid var(--ds-color-admin-border)",
                  boxShadow: "0 1px 0 0 var(--ds-color-admin-border)",
                }}
              >
                <div className="text-[10px] uppercase tracking-[0.14em] mb-3" style={{ color: "var(--ds-color-ink-muted)" }}>
                  Trial conversion
                </div>
                <div className="flex items-baseline gap-3">
                  <div className="font-display text-[30px]" style={{ color: "var(--ds-color-ink)" }}>
                    {pct(trial.conversion_rate)}
                  </div>
                  <div className="text-xs" style={{ color: "var(--ds-color-ink-muted)" }}>
                    {fmtInt(trial.trials_converted)} / {fmtInt(trial.trials_started)} converted
                  </div>
                </div>
                <div className="text-[11px] mt-3" style={{ color: "var(--ds-color-ink-muted)" }}>
                  Churned in trial: {fmtInt(trial.trials_churned)} · Still in trial: {fmtInt(trial.trials_still_in_trial)}
                </div>
              </div>
              <div
                data-testid="revenue-churn-card"
                className="rounded-[var(--ds-radius-card)] p-5"
                style={{
                  background: "var(--ds-color-surface)",
                  border: "1px solid var(--ds-color-admin-border)",
                  boxShadow: "0 1px 0 0 var(--ds-color-admin-border)",
                }}
              >
                <div className="text-[10px] uppercase tracking-[0.14em] mb-3" style={{ color: "var(--ds-color-ink-muted)" }}>
                  Churn rate
                </div>
                <div className="flex items-baseline gap-3">
                  <div
                    className="font-display text-[30px]"
                    style={{ color: churn.net_churn_rate > 0.05 ? "var(--ds-color-danger)" : "var(--ds-color-ink)" }}
                  >
                    {pct(churn.net_churn_rate)}
                  </div>
                  <div className="text-xs" style={{ color: "var(--ds-color-ink-muted)" }}>
                    net · gross {pct(churn.gross_churn_rate)}
                  </div>
                </div>
                <div className="text-[11px] mt-3" style={{ color: "var(--ds-color-ink-muted)" }}>
                  {fmtInt(churn.canceled_in_period)} canceled of {fmtInt(churn.starting_active)} starting active
                  {churn.reactivations > 0 ? ` · ${fmtInt(churn.reactivations)} reactivated` : ""}
                </div>
              </div>
            </div>

            {/* Row 3 — timeseries chart */}
            <Section
              title={`Time series — ${METRIC_OPTIONS.find(o => o.value === metric)?.label} · last ${tsRange} · ${granularity}`}
              testid="revenue-timeseries-section"
              mb="mb-6"
            >
              <div className="flex items-center gap-2 flex-wrap mb-4" data-testid="revenue-timeseries-chart">
                <SegmentedFilter options={METRIC_OPTIONS} value={metric} onChange={setMetric} testIdPrefix="revenue-metric" />
                <SegmentedFilter options={RANGE_OPTIONS.slice(1)} value={tsRange} onChange={setTsRange} testIdPrefix="revenue-ts-range" />
                <SegmentedFilter options={GRANULARITY_OPTIONS} value={granularity} onChange={setGranularity} testIdPrefix="revenue-granularity" />
              </div>
              <div style={{ width: "100%", height: 260 }}>
                {seriesLoading ? (
                  <div className="w-full h-full flex items-center justify-center" style={{ color: "var(--ds-color-ink-muted)" }}>
                    <Loader2 className="w-4 h-4 animate-spin" />
                  </div>
                ) : chartData.length === 0 ? (
                  <div className="w-full h-full flex items-center justify-center text-xs" style={{ color: "var(--ds-color-ink-muted)" }}>
                    No data in this range.
                  </div>
                ) : (
                  <ResponsiveContainer>
                    <LineChart data={chartData}>
                      <CartesianGrid stroke="var(--ds-color-admin-border)" strokeDasharray="3 3" />
                      <XAxis dataKey="date" stroke="var(--ds-color-ink-subtle)" fontSize={10} tickMargin={6} />
                      <YAxis stroke="var(--ds-color-ink-subtle)" fontSize={10} tickMargin={4}
                             tickFormatter={v => metric === "mrr" ? `$${v.toFixed(0)}` : `${v}`} />
                      <Tooltip
                        contentStyle={{ background: "var(--ds-color-surface)", border: "1px solid var(--ds-color-admin-border)", borderRadius: "var(--ds-radius-input)", fontSize: 12, color: "var(--ds-color-ink)" }}
                        formatter={(v) => metric === "mrr" ? `$${Number(v).toFixed(2)}` : v}
                      />
                      <Line type="monotone" dataKey="value" stroke="var(--ds-color-accent-strong)" strokeWidth={2} dot={{ r: 2 }} />
                    </LineChart>
                  </ResponsiveContainer>
                )}
              </div>
            </Section>

            {/* Row 4 — coupon impact */}
            <Section
              title={`Coupon impact — ${money(coup.total_discount_given_cents)} discounted in the last ${range}`}
              testid="revenue-coupon-impact-widget"
              mb="mb-6"
            >
              <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
                <div className="text-[11px]" style={{ color: "var(--ds-color-ink-muted)" }}>
                  {pct(coup.discount_share)} of gross revenue given up · {money(coup.revenue_before_discount_cents)} → {money(coup.revenue_after_discount_cents)}
                </div>
                <div className="w-full sm:w-[220px] h-20 flex-shrink-0">
                  <ResponsiveContainer>
                    <BarChart data={[
                      { name: "Before", value: coup.revenue_before_discount_cents / 100 },
                      { name: "After", value: coup.revenue_after_discount_cents / 100 },
                    ]}>
                      <XAxis dataKey="name" stroke="var(--ds-color-ink-subtle)" fontSize={9} />
                      <Tooltip formatter={(v) => `$${Number(v).toFixed(2)}`}
                               contentStyle={{ background: "var(--ds-color-surface)", border: "1px solid var(--ds-color-admin-border)", borderRadius: "var(--ds-radius-input)", fontSize: 12, color: "var(--ds-color-ink)" }} />
                      <Bar dataKey="value" fill="var(--ds-color-accent-strong)" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-left text-[10px] uppercase tracking-[0.14em]" style={{ color: "var(--ds-color-ink-muted)" }}>
                      <th className="py-2 pr-2">Code</th>
                      <th className="py-2 pr-2">Redemptions</th>
                      <th className="py-2 pr-2">Discount given</th>
                    </tr>
                  </thead>
                  <tbody>
                    {coup.top_coupons.length === 0 ? (
                      <tr><td colSpan={3} className="py-3" style={{ color: "var(--ds-color-ink-muted)" }}>No coupon redemptions in this window.</td></tr>
                    ) : coup.top_coupons.map((c, i, arr) => {
                      const isLast = i === arr.length - 1;
                      return (
                        <tr
                          key={c.code}
                          style={{ borderBottom: isLast ? "none" : "1px solid var(--ds-color-admin-border)" }}
                          data-testid={`revenue-top-coupon-${c.code}`}
                        >
                          <td className="px-4 py-1.5 font-mono tabular-nums" style={{ color: "var(--ds-color-ink)" }}>{c.code}</td>
                          <td className="px-4 py-1.5 tabular-nums" style={{ color: "var(--ds-color-ink-muted)" }}>{fmtInt(c.redemptions)}</td>
                          <td className="px-4 py-1.5 tabular-nums" style={{ color: "var(--ds-color-ink)" }}>{money(c.discount_given_cents)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </Section>

            {/* Row 5 — top churn reasons */}
            <Section
              title="Top churn reasons (last 30d) — why members left"
              testid="revenue-churn-reasons"
            >
              <div className="flex items-start justify-between gap-3 mb-4">
                <div className="text-[11px]" style={{ color: "var(--ds-color-ink-muted)" }}>
                  Ranked share of cancellations by declared reason.
                </div>
                <Link
                  to="/admin/churn"
                  data-testid="revenue-churn-details-link"
                  className="inline-flex items-center gap-1 text-xs hover:underline"
                  style={{ color: "var(--ds-color-accent)" }}
                >
                  Full details <ExternalLink className="w-3 h-3" />
                </Link>
              </div>
              {churnReasons.length === 0 ? (
                <div className="text-xs" style={{ color: "var(--ds-color-ink-muted)" }}>No cancellations recorded — thankfully!</div>
              ) : (
                <div className="space-y-2">
                  {churnReasons.map(r => (
                    <div key={r.reason_code} className="flex items-center gap-3 min-w-0">
                      <div className="w-32 sm:w-40 text-xs truncate flex-shrink-0" style={{ color: "var(--ds-color-ink)" }}>
                        {r.reason_label}
                      </div>
                      <div
                        className="flex-1 min-w-0 h-1.5 rounded-full overflow-hidden"
                        style={{ background: "var(--ds-color-admin-surface-muted)" }}
                      >
                        <div className="h-full" style={{
                          width: `${Math.min(100, r.share * 100)}%`,
                          background: r.is_alert ? "var(--ds-color-danger)" : "var(--ds-color-accent-strong)",
                        }} />
                      </div>
                      <div className="w-16 sm:w-20 text-right text-xs flex-shrink-0 tabular-nums" style={{ color: "var(--ds-color-ink-muted)" }}>
                        {pct(r.share)} · {r.count}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Section>
          </>
        ) : null}
      </div>
    </AdminLayout>
  );
}
